import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { KitLabBackendKit } from 'Src/helpers/service/admin/kitLab';
import { Team } from 'Src/models/team';
import { GENDER } from 'Src/consts/common';
import {
  KitBottomPattern,
  DetailColorSource,
  KitPatternVariant,
  KitSkortSideTrianglesMode,
  ShortsSideTrianglesPosition,
  SleevePattern,
  Gender,
  CollarStyle
} from 'Src/types/kitLab';

export const OUTLINE_COLOR = '#e5e9f4';
export const OUTLINE_WIDTH = 0.6;

export const outlineStrokeProps = {
  stroke: OUTLINE_COLOR,
  strokeWidth: OUTLINE_WIDTH,
  strokeLinejoin: 'round' as const,
  vectorEffect: 'non-scaling-stroke' as const
};

export interface KitPreset {
  id: string;
  name: string;

  gender: Gender;
  shirtPattern: KitPatternVariant;
  bottomPattern: KitBottomPattern;

  primaryColor: string;
  secondaryColor: string;
  shortsPrimaryColor: string;
  shortsSecondaryColor: string;

  showPocket: boolean;
  showCollar: boolean;
  showSleeves: boolean;
  showShoulderTriangles: boolean;
  showSleeveCuffs: boolean;

  pocketColorSource: DetailColorSource;
  sleevesColorSource: DetailColorSource;
  collarColorSource: DetailColorSource;
  shoulderTrianglesColorSource: DetailColorSource;
  sleeveCuffsColorSource: DetailColorSource;

  pocketCustomColor: string;
  sleevesCustomColor: string;
  collarCustomColor: string;
  shoulderTrianglesCustomColor: string;
  sleeveCuffsCustomColor: string;

  collarStyle: CollarStyle;

  stripeCount: number;
  stripesEqualWidth: boolean;
  stripePattern: number[];

  skortStripeCount: number;
  skortStripesEqualWidth: boolean;
  skortStripePattern: number[];

  diagonalDirection: 'LEFT' | 'RIGHT';
  chevronSize: 'SMALL' | 'BIG';
  chevronDirection: 'UP' | 'DOWN';
  chevronPosition: 'TOP' | 'MIDDLE' | 'BOTTOM';
  sideTrianglesMode: 'TOP' | 'BOTTOM' | 'BOTH';
  skortSideTrianglesMode: 'TOP' | 'BOTTOM' | 'BOTH';
  shortsTrianglesMode: ShortsSideTrianglesPosition;
  sleevePattern: SleevePattern;

  isDefaultBoys?: boolean;
  isDefaultGirls?: boolean;

  sportIds: string[];
}

export interface LayoutPresetObj {
  shirtPattern: KitPatternVariant;
  bottomPattern: KitBottomPattern;

  primaryColor: string;
  secondaryColor: string;
  shortsPrimaryColor: string;
  shortsSecondaryColor: string;

  showPocket: boolean;
  showCollar: boolean;
  showSleeves: boolean;
  showShoulderTriangles: boolean;
  showSleeveCuffs: boolean;

  pocketColorSource: DetailColorSource;
  sleevesColorSource: DetailColorSource;
  collarColorSource: DetailColorSource;
  shoulderTrianglesColorSource: DetailColorSource;
  sleeveCuffsColorSource: DetailColorSource;

  pocketCustomColor: string;
  sleevesCustomColor: string;
  collarCustomColor: string;
  shoulderTrianglesCustomColor: string;
  sleeveCuffsCustomColor: string;

  collarStyle: 'ROUND' | 'POLO';

  stripeCount: number;
  stripesEqualWidth: boolean;
  stripePattern: number[];

  skortStripeCount: number;
  skortStripesEqualWidth: boolean;
  skortStripePattern: number[];

  diagonalDirection: 'LEFT' | 'RIGHT';
  chevronSize: 'SMALL' | 'BIG';
  chevronDirection: 'UP' | 'DOWN';
  chevronPosition: 'TOP' | 'MIDDLE' | 'BOTTOM';
  sideTrianglesMode: 'TOP' | 'BOTTOM' | 'BOTH';
  skortSideTrianglesMode: KitSkortSideTrianglesMode;
  shortsTrianglesMode: ShortsSideTrianglesPosition;
  sleevePattern: SleevePattern;
}

export const DEFAULT_LAYOUT: LayoutPresetObj = {
  shirtPattern: 'plain',
  bottomPattern: 'SHORTS_PLAIN',

  primaryColor: '#1f78ff',
  secondaryColor: '#ffd24d',
  shortsPrimaryColor: '#1f78ff',
  shortsSecondaryColor: '#ffd24d',

  showPocket: true,
  showCollar: true,
  showSleeves: true,
  showShoulderTriangles: false,
  showSleeveCuffs: false,

  pocketColorSource: 'SECONDARY',
  sleevesColorSource: 'SECONDARY',
  collarColorSource: 'SECONDARY',
  shoulderTrianglesColorSource: 'SECONDARY',
  sleeveCuffsColorSource: 'SECONDARY',

  pocketCustomColor: '#ffffff',
  sleevesCustomColor: '#ffffff',
  collarCustomColor: '#ffffff',
  shoulderTrianglesCustomColor: '#ffffff',
  sleeveCuffsCustomColor: '#ffffff',

  collarStyle: 'ROUND',

  stripeCount: 5,
  stripesEqualWidth: true,
  stripePattern: [1, 1, 1, 1, 1],

  skortStripeCount: 5,
  skortStripesEqualWidth: true,
  skortStripePattern: [1, 1, 1, 1, 1],

  diagonalDirection: 'RIGHT',
  chevronSize: 'SMALL',
  chevronDirection: 'DOWN',
  chevronPosition: 'MIDDLE',
  sideTrianglesMode: 'BOTH',
  skortSideTrianglesMode: 'BOTH',
  shortsTrianglesMode: 'BOTH',
  sleevePattern: 'PLAIN'
};

export const SHIRT_PATTERNS: {
  id: KitPatternVariant;
  label: string;
}[] = [
  { id: 'plain', label: 'Plain' },
  { id: 'verticalStripes', label: 'Vertical stripes' },
  { id: 'horizontalStripes', label: 'Horizontal stripes' },
  { id: 'diagonal', label: 'Diagonal stripes' },
  { id: 'sideTriangles', label: 'Side triangles' },
  { id: 'chevron', label: 'Chevron' },
  { id: 'sidePanels', label: 'Side panels' },
  { id: 'sideThinStripes', label: 'Side thin stripes' },
  { id: 'sideSemiCircles', label: 'Side semi-circles' }
];

export const BOTTOM_PATTERNS: { id: KitBottomPattern; label: string }[] = [
  { id: 'SHORTS_PLAIN', label: 'Plain shorts' },
  { id: 'SHORTS_SIDE', label: 'Side stripes' },
  { id: 'SHORTS_TRIANGLES', label: 'Top/bottom triangles' },
  { id: 'SHORTS_BIG_TRIANGLES', label: 'Side triangles' },
  { id: 'SHORTS_SEMICIRCLES', label: 'Side semi-circles' },

  { id: 'SKORT_PLAIN', label: 'Plain skort' },
  { id: 'SKORT_VERTICAL_STRIPES', label: 'Striped skort' },
  { id: 'SKORT_SIDE', label: 'Side stripes' },
  { id: 'SKORT_TRIANGLES', label: 'Top/bottom triangles' },
  { id: 'SKORT_BOTTOM_TRIANGLES', label: 'Side triangles' },
  { id: 'SKORT_SEMICIRCLES', label: 'Side semi-circles' }
];

export function getKitGenderForTeam(teamGender?: string): string | undefined {
  switch (teamGender) {
    case GENDER.MALE_ONLY:
      return 'MALE';
    case GENDER.FEMALE_ONLY:
      return 'FEMALE';
    case GENDER.MIXED:
    default:
      return undefined;
  }
}

export function isKitSuitableForTeam(kit: KitLabBackendKit, team: Team): boolean {
  const teamGender = team.gender;
  const teamSportId = team.sportId?.toString();

  if (kit.gender) {
    const kitGender = kit.gender.toUpperCase();
    if (teamGender === GENDER.MALE_ONLY && kitGender !== 'MALE') return false;
    if (teamGender === GENDER.FEMALE_ONLY && kitGender !== 'FEMALE') return false;
  }

  if (Array.isArray(kit.sportIds) && kit.sportIds.length > 0 && teamSportId) {
    const sportIds = kit.sportIds.map(id => id.toString());
    if (!sportIds.includes(teamSportId)) {
      return false;
    }
  }

  return true;
}

export interface KitSummary {
  _id: any;
  gender: string;
  sportIds?: any[];
  isDefault?: boolean;
}

export type EventGender = 'MALE_ONLY' | 'FEMALE_ONLY' | 'MIXED';

type KitGender = 'MALE' | 'FEMALE';

function pickKitForSingleGender(
  kits: KitSummary[],
  sportId: string | null | undefined,
  gender: KitGender
): KitSummary | null {
  if (!kits || kits.length === 0) return null;

  const genderOk = (k: KitSummary) => (k.gender || '').toUpperCase() === gender;

  const sameSport = (k: KitSummary) => {
    if (!sportId) return false;
    if (!Array.isArray(k.sportIds) || k.sportIds.length === 0) return false;
    const sportIdStr = sportId.toString();
    return k.sportIds.some(id => id && id.toString() === sportIdStr);
  };

  const noSports = (k: KitSummary) => !k.sportIds || k.sportIds.length === 0;

  const sportKits = kits.filter(k => genderOk(k) && sameSport(k));
  const sportDefault = sportKits.find(k => k.isDefault);
  if (sportDefault) return sportDefault;
  if (sportKits.length > 0) return sportKits[0];

  const genericKits = kits.filter(k => genderOk(k) && noSports(k));
  const genericDefault = genericKits.find(k => k.isDefault);
  if (genericDefault) return genericDefault;
  if (genericKits.length > 0) return genericKits[0];

  const anyDefault = kits.find(k => genderOk(k) && k.isDefault);
  if (anyDefault) return anyDefault;

  const anyByGender = kits.find(k => genderOk(k));
  if (anyByGender) return anyByGender;

  return null;
}

export function pickKitForEvent(
  kits: KitSummary[],
  sportId: string | null | undefined,
  eventGender: EventGender
): KitSummary | null {
  if (!kits || kits.length === 0) return null;

  if (eventGender === 'MALE_ONLY') {
    return pickKitForSingleGender(kits, sportId, 'MALE');
  }

  if (eventGender === 'FEMALE_ONLY') {
    return pickKitForSingleGender(kits, sportId, 'FEMALE');
  }

  const boysKit = pickKitForSingleGender(kits, sportId, 'MALE');
  if (boysKit) return boysKit;

  const girlsKit = pickKitForSingleGender(kits, sportId, 'FEMALE');
  if (girlsKit) return girlsKit;

  return null;
}

export function isSkortPatternId(pattern?: KitBottomPattern): boolean {
  if (!pattern) return false;
  return (
    pattern === 'SKORT_PLAIN' ||
    pattern === 'SKORT_VERTICAL_STRIPES' ||
    pattern === 'SKORT_SIDE' ||
    pattern === 'SKORT_TRIANGLES' ||
    pattern === 'SKORT_BOTTOM_TRIANGLES' ||
    pattern === 'SKORT_SEMICIRCLES'
  );
}

export function mapBottomToSkortPattern(
  pattern: KitBottomPattern
): 'plain' | 'verticalStripes' | 'sideStripes' | 'sideTriangles' | 'sideBottomTriangles' | 'sideSemiCircles' {
  switch (pattern) {
    case 'SKORT_VERTICAL_STRIPES':
      return 'verticalStripes';
    case 'SKORT_SIDE':
      return 'sideStripes';
    case 'SKORT_TRIANGLES':
      return 'sideTriangles';
    case 'SKORT_BOTTOM_TRIANGLES':
      return 'sideBottomTriangles';
    case 'SKORT_SEMICIRCLES':
      return 'sideSemiCircles';
    case 'SKORT_PLAIN':
    default:
      return 'plain';
  }
}

export function getDefaultGenderFromUser(user: AppUser): Gender {
  const schoolGender =
    propz.get(user, ['school', 'gender']) ||
    propz.get(user, ['currentSchool', 'gender']) ||
    propz.get(user, ['schoolLike', 'gender']);

  if (schoolGender === 'FEMALE') return 'FEMALE';
  if (schoolGender === 'MALE') return 'MALE';
  return 'MALE';
}

export function getDefaultConfig(): Omit<KitPreset, 'id' | 'name' | 'isDefaultBoys' | 'isDefaultGirls'> {
  return {
    gender: 'MALE',
    shirtPattern: 'plain',
    bottomPattern: 'SHORTS_PLAIN',

    primaryColor: '#1f78ff',
    secondaryColor: '#ffd24d',
    shortsPrimaryColor: '#1f78ff',
    shortsSecondaryColor: '#ffd24d',

    showPocket: true,
    showCollar: true,
    showSleeves: true,
    showShoulderTriangles: false,
    showSleeveCuffs: false,

    pocketColorSource: 'SECONDARY',
    sleevesColorSource: 'SECONDARY',
    collarColorSource: 'SECONDARY',
    shoulderTrianglesColorSource: 'SECONDARY',
    sleeveCuffsColorSource: 'SECONDARY',

    pocketCustomColor: '#ffffff',
    sleevesCustomColor: '#ffffff',
    collarCustomColor: '#ffffff',
    shoulderTrianglesCustomColor: '#ffffff',
    sleeveCuffsCustomColor: '#ffffff',

    collarStyle: 'ROUND',

    stripeCount: 5,
    stripesEqualWidth: true,
    stripePattern: [1, 1, 1, 1, 1],

    skortStripeCount: 5,
    skortStripesEqualWidth: true,
    skortStripePattern: [1, 1, 1, 1, 1],

    diagonalDirection: 'RIGHT',
    chevronSize: 'SMALL',
    chevronDirection: 'DOWN',
    chevronPosition: 'MIDDLE',
    sideTrianglesMode: 'BOTH',
    skortSideTrianglesMode: 'BOTH',
    shortsTrianglesMode: 'BOTH',
    sleevePattern: 'PLAIN',
    sportIds: []
  };
}

export function isSkortAllowedForGender(gender: Gender): boolean {
  return gender === 'FEMALE';
}

export function normalizeBottomPatternForGender(gender: Gender, pattern: KitBottomPattern): KitBottomPattern {
  if (!isSkortAllowedForGender(gender) && isSkortPatternId(pattern)) {
    return 'SHORTS_PLAIN';
  }
  return pattern;
}

export function resolveDetailColor(
  source: DetailColorSource,
  custom: string,
  primary: string,
  secondary: string
): string {
  if (source === 'PRIMARY') return primary;
  if (source === 'SECONDARY') return secondary;
  return custom || secondary || primary;
}

export function getLayoutForKit(kit: KitLabBackendKit): LayoutPresetObj {
  let base: LayoutPresetObj = { ...DEFAULT_LAYOUT };

  if (kit.layoutPreset) {
    try {
      const parsed = JSON.parse(kit.layoutPreset);
      if (parsed && typeof parsed === 'object') {
        base = { ...base, ...(parsed as Partial<LayoutPresetObj>) };
      }
    } catch (e) {}
  } else if (Array.isArray(kit.segments)) {
    const primarySeg = kit.segments.find(s => s.segmentKey === 'SHIRT_PRIMARY');
    const secondarySeg = kit.segments.find(s => s.segmentKey === 'SHIRT_SECONDARY');
    const shortsPrimarySeg = kit.segments.find(s => s.segmentKey === 'SHORTS_PRIMARY');
    const shortsSecondarySeg = kit.segments.find(s => s.segmentKey === 'SHORTS_SECONDARY');

    base.primaryColor = primarySeg?.color || base.primaryColor;
    base.secondaryColor = secondarySeg?.color || base.secondaryColor;
    base.shortsPrimaryColor = shortsPrimarySeg?.color || base.shortsPrimaryColor;
    base.shortsSecondaryColor = shortsSecondarySeg?.color || base.shortsSecondaryColor;
  }

  return base;
}

